'use client'

import {useTranslations} from 'next-intl'
import {useCallback, useState} from 'react'

import {buttonClasses} from '@/app/[locale]/(website)/components/ui/ButtonClasses'
import {usePathname} from '@/i18n/routing'

// import {usePathname} from '@/navigation'

export const ContactForm = () => {
    const t = useTranslations('Contact')
    const path = usePathname()
    const [formData, setFormData] = useState({
        firstname: '',
        lastname: '',
        email: '',
        mobilephone: '',
        message: '',
        gdpr: false,
    })
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [isSubmitted, setIsSubmitted] = useState(false)
    const [error, setError] = useState<string | null>(null)

    const handleChange = useCallback(
        (e: {target: {name: string; value?: any; type: string; checked?: boolean}}) => {
            const {name, value, type, checked} = e.target
            setFormData((prevData) => ({
                ...prevData,
                [name]: type === 'checkbox' ? checked : value,
            }))
        },
        [],
    )

    const handleSubmit = useCallback(
        async (e: {preventDefault: () => void}) => {
            e.preventDefault()
            setIsSubmitting(true)
            setError(null)

            const payload = {
                fields: [
                    {name: 'email', value: formData.email},
                    {name: 'firstname', value: formData.firstname},
                    {name: 'lastname', value: formData.lastname},
                    {name: 'mobilephone', value: formData.mobilephone},
                    {name: 'message', value: formData.message},
                ],
                context: {
                    pageUri: `www.joinflux.nl${path}`,
                    pageName: 'Contact Page',
                },
                legalConsentOptions: {
                    consent: {
                        consentToProcess: formData.gdpr,
                        text: 'I agree to allow Your Company to store and process my personal data.',
                    },
                },
            }

            try {
                const response = await fetch(
                    'https://api.hsforms.com/submissions/v3/integration/submit/7270869/ee853c81-2df8-4157-89e3-d6da7fb545e6',
                    {
                        method: 'POST',
                        headers: {
                            'Content-Type': 'application/json',
                        },
                        body: JSON.stringify(payload),
                    },
                )

                if (!response.ok) {
                    throw new Error('Submission failed')
                }

                setIsSubmitted(true)
            } catch (err) {
                setError(t('errorMessage'))
            } finally {
                setIsSubmitting(false)
            }
        },
        [
            formData.email,
            formData.firstname,
            formData.gdpr,
            formData.lastname,
            formData.message,
            formData.mobilephone,
            path,
            t,
        ],
    )

    if (isSubmitted) {
        return <div>{t('thankYouMessage')}</div>
    }

    return (
        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-y-6 gap-x-3">
            {error && <div className="col-span-2 text-red-600">{error}</div>}
            <input
                type="text"
                name="firstname"
                value={formData.firstname}
                onChange={handleChange}
                placeholder={t('firstNamePlaceholder')}
                required
                className="col-span-2 md:col-span-1 border shadow-sm p-2 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
            />
            <input
                type="text"
                name="lastname"
                value={formData.lastname}
                onChange={handleChange}
                placeholder={t('lastNamePlaceholder')}
                required
                className="col-span-2 md:col-span-1 border shadow-sm p-2 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
            />
            <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder={t('emailPlaceholder')}
                required
                className="col-span-2 border shadow-sm p-2 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
            />
            <input
                type="tel"
                name="mobilephone"
                value={formData.mobilephone}
                onChange={handleChange}
                placeholder={t('phonePlaceholder')}
                className="col-span-2 border shadow-sm p-2 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
            />
            <textarea
                name="message"
                value={formData.message}
                onChange={handleChange}
                placeholder={t('messagePlaceholder')}
                required
                className="col-span-2 border shadow-sm p-2 placeholder-gray-400 focus:ring-blue-500 focus:border-blue-500 border-gray-300 rounded-md"
                rows={4}
            />
            <div className="col-span-2 flex items-center gap-1">
                <input
                    type="checkbox"
                    name="gdpr"
                    checked={formData.gdpr}
                    onChange={handleChange}
                    required
                    id="gdpr"
                    className="h-fit"
                />
                <label className="ml-1" htmlFor="gdpr">
                    {t('gdprConsentText')}
                </label>
            </div>
            <button
                type="submit"
                disabled={isSubmitting}
                className={buttonClasses({
                    variant: 'submit',
                    size: 'medium',
                    shape: 'rounded',
                    width: 'full',
                })}
            >
                {isSubmitting ? t('submittingText') : t('submitButtonText')}
            </button>
        </form>
    )
}
